import { WildfireRiskRating, getWildfireRiskText } from '@/features/types';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { HelpPopover } from '@/components/HelpPopover/HelpPopover';
import { useDatasets } from '@/hooks/useDatasets';
import { useState, useMemo } from 'react';
import { isValidUrl } from '@/features/utils';
import {
  Stack,
  Group,
  Text,
  Flex,
  Badge,
  Popover,
  useMantineTheme,
} from '@mantine/core';
import { getRasterSourceForType } from '@/views/map/layers';
import { LayerType } from '@/views/map/layers/types';
import { useTheme } from '@/assets/styles/themeMantine';
import { GoogleEvents } from '@/features/googleEvents';
import TagManager from 'react-gtm-module';
import { Icon } from '@/components/Icon/Icon';
import classes from '@/components/HelpPopover/HelpPopover.module.css';

const wildfireRiskColors = [
  { rating: WildfireRiskRating.Very_high, color: '#A80000' },
  { rating: WildfireRiskRating.High, color: '#FF0000' },
  { rating: WildfireRiskRating.Medium, color: '#FFA900' },
  { rating: WildfireRiskRating.Low, color: '#FFFF74' },
  { rating: WildfireRiskRating.Very_low, color: '#C8D7B5' },
];

export const WildfireLegend = () => {
  const { datasets } = useDatasets();
  const isSmallScreen = useSmallScreen();
  const theme = useTheme();
  const mantineTheme = useMantineTheme();

  const wildfireRisk = getRasterSourceForType(
    LayerType.wildfire_risk,
    datasets
  );
  const [popoverOpened, setPopoverOpened] = useState(false);

  const { helpUrl, sourceName } = useMemo(() => {
    if (!wildfireRisk?.description) {
      return { helpUrl: null, sourceName: 'external source' };
    }

    // Try to parse as JSON first
    try {
      const sourceData = JSON.parse(wildfireRisk.description);
      if (sourceData.source_name) {
        const url =
          sourceData.source_url && isValidUrl(sourceData.source_url)
            ? sourceData.source_url
            : null;
        return { helpUrl: url, sourceName: sourceData.source_name };
      }
    } catch {
      // Fallback to old format (direct URL)
      if (isValidUrl(wildfireRisk.description)) {
        return {
          helpUrl: wildfireRisk.description,
          sourceName: 'external source',
        };
      }
    }

    return { helpUrl: null, sourceName: 'external source' };
  }, [wildfireRisk?.description]);

  return (
    <Stack gap="xs" data-testid="wildfire-legend">
      {!isSmallScreen && (
        <Group justify="space-between" gap={5}>
          <Text fw={theme.other.typography.weight.medium} size="sm">
            Wildfire risk zones
          </Text>
          {(helpUrl || sourceName !== 'external source') &&
            (helpUrl ? (
              <HelpPopover
                link={helpUrl}
                linkText={sourceName}
                fontSize="sm"
                preText="Data from"
                onOpen={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GoogleEvents.wildfireHelpTooltipOpened,
                    },
                  });
                }}
              />
            ) : (
              <Popover
                opened={popoverOpened}
                onChange={setPopoverOpened}
                position="top"
                withArrow
                offset={10}
                classNames={{
                  dropdown: classes.dropdown,
                  arrow: classes.arrow,
                }}
                styles={{
                  dropdown: {
                    background: mantineTheme.other.overlay.background,
                    color: mantineTheme.other.mainColor.foreground.onAccent,
                    ...mantineTheme.other.typography.variants.bodySmallRegular,
                  },
                  arrow: {
                    background: mantineTheme.other.overlay.background,
                  },
                }}
              >
                <Popover.Target>
                  <Flex
                    onClick={() => {
                      const newOpened = !popoverOpened;
                      if (newOpened) {
                        TagManager.dataLayer({
                          dataLayer: {
                            event: GoogleEvents.wildfireHelpTooltipOpened,
                          },
                        });
                      }
                      setPopoverOpened(newOpened);
                    }}
                    className={classes.helpIcon}
                  >
                    <Icon name="HelpGray" size={16}></Icon>
                  </Flex>
                </Popover.Target>
                <Popover.Dropdown>
                  <span>Data from {sourceName}</span>
                </Popover.Dropdown>
              </Popover>
            ))}
        </Group>
      )}
      <Flex gap="12px 16px" w="100%" wrap="wrap">
        {wildfireRiskColors.map((rating, index) => (
          <Flex gap={4} key={index} align="center">
            <Badge
              radius="1px"
              w={16}
              h={16}
              px={0}
              bd={1}
              color={rating.color}
            />
            <Text size="xs" c="dimmed" lineClamp={1} miw="min-content">
              {getWildfireRiskText(rating.rating)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Stack>
  );
};
